<template>
  <!-- The iPhone app, drawn with three of the App Store listing's own screenshots (data/mobile-app-facts.ts
       workspaceMobileScreenshots: Home, a client page and the projects list). They are the real app
       with the listing's sample data, so nothing here is re-drawn. The current state's screenshot sits
       in front; the other two wait behind it, left and right. Only transform and opacity change, so
       the stage never resizes. States:
         dashboard  Home: tasks due today, events today, reminders, revenue and clients
         client     a client page: open invoices, outstanding, next visit, active projects
         projects   the projects list: status, priority, progress, budget and due dates -->
  <div class="mbs" :class="`mbs--${state}`">
    <figure
      v-for="(shot, i) in shots"
      :key="shot.key"
      class="mbs-card"
      :class="`mbs-slot-${slot(i)}`"
    >
      <img
        :src="shot.srcSmall"
        :srcset="`${shot.srcSmall} 300w, ${shot.src} 600w`"
        sizes="(max-width: 639px) 170px, 186px"
        width="300"
        height="652"
        :alt="shot.alt"
        :loading="shot.key === 'dashboard' ? 'eager' : 'lazy'"
        :fetchpriority="hero && shot.key === 'dashboard' ? 'high' : undefined"
        decoding="async"
      />
    </figure>
  </div>
</template>

<script setup>
import { workspaceMobileScreenshots } from "~/data/mobile-app-facts";

const props = defineProps({
  state: { type: String, default: "dashboard" },
});

// In the hero StoryStage the dashboard shot is the page's LCP image.
const hero = inject("storyStageHero", false);

// The facts file lists them as Home, client page, projects.
const keys = ["dashboard", "client", "projects"];
const shots = workspaceMobileScreenshots.map((shot, i) => ({ ...shot, key: keys[i] }));

// 0 = front, 1 = behind on the right, 2 = behind on the left.
const slot = (i) => {
  const front = Math.max(0, keys.indexOf(props.state));
  return (i - front + keys.length) % keys.length;
};
</script>

<style scoped>
.mbs { position: relative; display: grid; place-items: center; height: 100%; }
.mbs-card {
  grid-area: 1 / 1;
  width: 186px;
  margin: 0;
  overflow: hidden;
  border: 1px solid rgb(0 0 0 / 0.08);
  border-radius: 10px;
  background: #e8f1f8;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.35), 0 2px 6px rgb(15 23 42 / 0.08);
  transition: transform 0.7s cubic-bezier(0.33, 1, 0.68, 1);
}
.dark .mbs-card { border-color: rgb(255 255 255 / 0.12); box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.75); }
.mbs-card { position: relative; }
.mbs-card img { display: block; width: 100%; height: auto; }
/* The cards behind are dimmed by a veil, so a card never shows through the one in front. */
.mbs-card::after { content: ""; position: absolute; inset: 0; background: rgb(15 23 42 / 0.22); opacity: 1; transition: opacity 0.5s ease; }
.mbs-slot-0 { z-index: 3; transform: none; }
.mbs-slot-0::after { opacity: 0; }
.mbs-slot-1 { z-index: 2; transform: translateX(62%) scale(0.84); }
.mbs-slot-2 { z-index: 1; transform: translateX(-62%) scale(0.84); }

@media (max-width: 639px) {
  .mbs-card { width: 170px; }
  .mbs-slot-1 { transform: translateX(40%) scale(0.8); }
  .mbs-slot-2 { transform: translateX(-40%) scale(0.8); }
}
@media (prefers-reduced-motion: reduce) {
  .mbs-card, .mbs-card::after { transition: none; }
}
</style>
